import type { ReactNode } from 'react'
import type { Source } from '../../api/client'
import { Card } from '../card/Card'
import { Chip } from './Chip'
import s from './Belege.module.scss'

/**
 * Die Belege — woher eine Angabe stammt.
 *
 * EIN BAUTEIL FÜR VIER STELLEN: Recherchefälle, Meldungen, Ereignisse und
 * Handelsideen zeigen ihre Belege gleich, als Karte „Belege" unter dem
 * Detail. Die Fälle tragen mehr je Zeile — Quellenart, Amtlichkeit, Datum,
 * Zitat —, die drei anderen nur Kurztitel, Link und Name der Quelle. Die
 * Zeile kennt beides; was fehlt, bleibt weg.
 */

export interface Beleg {
  id: number | string
  /** Der Kurztitel — als Link, wenn es eine Adresse gibt. */
  titel: string | null
  url: string | null
  /** Die Marke vorn: bei Fällen die Quellenart, sonst der Name der Quelle. */
  marke?: string | null
  /** Amtliche Quellen wiegen schwerer — das steht in der Farbe. */
  amtlich?: boolean
  /** Was hinter dem Link steht, gedämpft — etwa das Datum. */
  neben?: string | null
  /** Das Original, nicht ein Bericht darüber. */
  original?: boolean
  zitat?: string | null
}

/** Die drei Felder der Belegtabelle, als Zeilen dieser Karte. */
export function alsBelege(quellen: Source[]): Beleg[] {
  return quellen.map((q) => ({
    id: q.id, titel: q.title, url: q.url, marke: q.publisher,
  }))
}

export function BelegeListe({ belege }: { belege: Beleg[] }) {
  return (
    <div className={s.belege}>
      {belege.map((b) => (
        <div key={b.id} className={s.beleg}>
          {b.marke && (
            <span className={s.marke} data-amtlich={b.amtlich ?? false}>
              {b.marke}
            </span>
          )}
          {b.url
            ? <a href={b.url} target="_blank" rel="noreferrer">
                {b.titel ?? b.url}
              </a>
            : <span>{b.titel ?? '—'}</span>}
          {b.neben && <span className={s.neben}>{' · '}{b.neben}</span>}
          {b.original && <Chip tone="outline">Original</Chip>}
          {b.zitat && <p className={s.zitat}>„{b.zitat}“</p>}
        </div>
      ))}
    </div>
  )
}

/**
 * Die Karte, wie die Fälle sie zeigen: volle Breite, Anzahl im Kopf.
 *
 * OHNE BELEGE KEINE KARTE. Eine leere Karte „Belege 0" behauptete, dass
 * hier etwas fehlt — bei einer Meldung ohne Belege fehlt nichts.
 */
export function BelegeKarte({ belege, help }: {
  belege: Beleg[]
  help?: ReactNode
}) {
  if (belege.length === 0) return null
  return (
    <Card title="Belege" span={12} meta={String(belege.length)} help={help}>
      <BelegeListe belege={belege} />
    </Card>
  )
}
